import type { Priority, TaskStatus, SprintStatus, UserRole } from '../types';

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'agora mesmo';
  if (diffMins < 60) return `${diffMins}min atrás`;
  if (diffHours < 24) return `${diffHours}h atrás`;
  if (diffDays < 7) return `${diffDays}d atrás`;
  return formatDate(dateStr);
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export function getPriorityConfig(priority: Priority) {
  const configs = {
    low: { label: 'Baixa', color: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400' },
    medium: { label: 'Média', color: 'bg-amber-50 text-amber-700', dot: 'bg-amber-400' },
    high: { label: 'Alta', color: 'bg-orange-50 text-orange-700', dot: 'bg-orange-500' },
    critical: { label: 'Crítica', color: 'bg-red-50 text-red-700', dot: 'bg-red-500' },
  };
  return configs[priority];
}

export function getStatusConfig(status: TaskStatus) {
  const configs = {
    backlog: { label: 'Backlog', color: 'bg-slate-100 text-slate-600', accent: '#94a3b8' },
    todo: { label: 'A Fazer', color: 'bg-blue-50 text-blue-700', accent: '#3b82f6' },
    in_progress: { label: 'Em Progresso', color: 'bg-brand-50 text-brand-700', accent: '#3570E6' },
    in_review: { label: 'Em Revisão', color: 'bg-amber-50 text-amber-700', accent: '#f59e0b' },
    done: { label: 'Concluído', color: 'bg-emerald-50 text-emerald-700', accent: '#10b981' },
  };
  return configs[status];
}

export function getSprintStatusConfig(status: SprintStatus) {
  const configs = {
    planning: { label: 'Planejamento', color: 'bg-slate-100 text-slate-700' },
    active: { label: 'Ativo', color: 'bg-brand-50 text-brand-700' },
    completed: { label: 'Concluído', color: 'bg-emerald-50 text-emerald-700' },
  };
  return configs[status];
}

export function getRoleConfig(role: UserRole) {
  const configs = {
    admin: { label: 'Admin', color: 'bg-red-50 text-red-700' },
    manager: { label: 'Gerente', color: 'bg-brand-50 text-brand-700' },
    developer: { label: 'Desenvolvedor', color: 'bg-emerald-50 text-emerald-700' },
    designer: { label: 'Designer', color: 'bg-pink-50 text-pink-700' },
    qa: { label: 'QA', color: 'bg-amber-50 text-amber-700' },
  };
  return configs[role];
}

export function generateId(): string {
  return Math.random().toString(36).substring(2, 11);
}

export function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

export function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

export function classNames(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

export function createObjectURL(file: File): string {
  return URL.createObjectURL(file);
}

export function isPreviewableFile(type: string): boolean {
  return (
    type.startsWith('image/') ||
    type === 'application/pdf' ||
    type.startsWith('video/')
  );
}
